import { sendVerificationEmail, verifyEmail,  } from 'supertokens-web-js/recipe/emailverification';
import { signOut, signUp } from 'supertokens-web-js/recipe/emailpassword';
import { signIn } from 'supertokens-web-js/recipe/emailpassword';
import { notifications } from '@mantine/notifications';
import type { AsyncAction, GenericDictionary } from 'src/state/_types';
import axios from 'axios'

export type AuthFormErrors = { form?: string; fields: Record<string, string> }
export type FormResponse = { success: boolean; errors: AuthFormErrors }

export const signUpAction: AsyncAction<GenericDictionary, FormResponse> = async ({actions}, payload) => {
    
    try {

        let response = await signUp({
            formFields: Object.entries(payload).map(([key, value]) => ({
                id: key,
                value: value
            }))
        })

        if (response.status === 'FIELD_ERROR') {

            notifications.show({
                title: 'Oops! Something went wrong.',
                message: 'Please check your form and try again.',
                color: 'red',
            })

            const fieldErrors: Record<string, string> = {}

            response.formFields.forEach(formField => {
                fieldErrors[formField.id] = formField.error
            })

            return { success: false, errors: { form: 'Please check your form and try again.', fields: fieldErrors } }
           
        } else if (response.status === 'SIGN_UP_NOT_ALLOWED') {
    
            notifications.show({
                title: 'Oops! Something went wrong.',
                message: `Not Allowed ${response.reason}`,
                color: 'red',
            })

            return { success: false, errors: { form: `Not Allowed ${response.reason}`, fields: {} } }

        } else {
            
            notifications.show({
                title: 'Welcome!',
                message: 'You have been signed up successfully. Please check your email for verification.',
                color: 'green',
            })

            await actions.sendVerificationLinkEmail()       

            return { success: true, errors: { fields: { } } }

        }

    } catch (err: any) {
        
        notifications.show({
            title: 'Oops! Something went wrong.',
            message: `Not Allowed ${err.message}`,
            color: 'red',
        })

        return { success: false, errors: { form: err.message, fields: { } } }

    }

}

type SignInPayload = {
    email: string
    password: string
}

export const signInAction: AsyncAction<SignInPayload, FormResponse> = async ({},payload) => {
    
    try {

        let response = await signIn({
            formFields: Object.entries(payload).map(([key, value]) => ({
                id: key,
                value: value
            }))
        })

        if (response.status === 'FIELD_ERROR') {
            
            notifications.show({
                title: 'Oops! Something went wrong.',
                message: 'Please check your form and try again.',
                color: 'red',
            })

            const fieldErrors: Record<string, string> = {}

            response.formFields.forEach(formField => {
                fieldErrors[formField.id] = formField.error
            })

            return { success: false, errors: { form: 'Please check your form and try again.', fields: fieldErrors } }

        } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
            
            notifications.show({
                title: 'Oops! Something went wrong.',
                message: 'Please check your email and password and try again.',
                color: 'red',
            })

            return { success: false, errors: { form: 'Please check your email and password and try again.', fields: { } } }

        } else if (response.status === 'SIGN_IN_NOT_ALLOWED') {
            
            notifications.show({
                title: 'Not Allowed',
                message: `${response.reason}`,
                color: 'red',
            })

            return { success: false, errors: { form: `Not Allowed: ${response.reason}`, fields: {} } }

        } else {
            
            notifications.show({
                title: 'Success!',
                message: 'You have been signed in successfully.',
                color: 'green',
            })

            return { success: true, errors: { fields: { } } }

        }

    } catch (err: any) {
        
        notifications.show({
            title: 'Oops! Something went wrong.',
            message: `Not Allowed ${err.message}`,
            color: 'red',
        })

        return { success: false, errors: { form: err.message, fields: { } } }

    }

}

export const signOutAction: AsyncAction = async () => {
    await signOut();
    notifications.show({
        title: 'Success!',
        message: 'You have been signed out successfully.',
        color: 'green',
    })
    window.location.assign('/')
}

export const sendVerificationLinkEmail: AsyncAction<void, boolean | undefined> = async (): Promise<boolean | undefined> => {

    try {

        let response = await sendVerificationEmail();

        if (response.status === 'EMAIL_ALREADY_VERIFIED_ERROR') {
            
            window.location.assign('/')

        } else {

            notifications.show({
                title: 'Success!',
                message: 'Email verification link sent successfully.',
                color: 'green',
            })

            return true

        }
    } catch (err: any) {
        
        notifications.show({
            title: 'Oops! Something went wrong.',
            message: `Not Allowed ${err.message}`,
            color: 'red',
        })

    }

}

export const verifyEmailAction: AsyncAction = async () => {   

    try {

        let response = await verifyEmail();

        if (response.status === 'EMAIL_VERIFICATION_INVALID_TOKEN_ERROR') {
            
            notifications.show({
                title: 'Oops!',
                message: 'Seems like the verification link expired. Please try again.',
                color: 'red',
            })

            await new Promise(resolve => setTimeout(resolve, 3000));

            window.location.assign('/verify-email')

        } else {

            notifications.show({
                title: 'Success!',
                message: 'Email verified successfully.',
                color: 'green',
            })

            window.location.assign('/')
        }
    } catch (err: any) {
        
        notifications.show({
            title: 'Oops! Something went wrong.',
            message: `Not Allowed ${err.message}`,
            color: 'red',
        })

    }

}

export const requestPasswordResetAction: AsyncAction<{ email: string }, boolean | undefined> = async ({}, payload) => {   
    
    try {

        await axios.post(`${import.meta.env.VITE_AUTH_API_HOST}/user/request-password-reset`, payload)

        notifications.show({
            title: 'Success!',
            message: 'Password reset link sent successfully.',
            color: 'green',
        })

        return true

    } catch (err: any) {
        
        notifications.show({
            title: 'Oops! Something went wrong.',
            message: `Not Allowed ${err.message}`,
            color: 'red',
        })
        
    }

}

export const resetPasswordAction: AsyncAction<{ password: string, token: string }, boolean | undefined> = async ({}, payload) => { 

    try {

        await axios.post(`${import.meta.env.VITE_AUTH_API_HOST}/user/reset-password`, payload)

        notifications.show({
            title: 'Success!',
            message: 'Password reset successfully.',
            color: 'green',
        })

        return true

    } catch (err: any) {
        
        notifications.show({
            title: 'Oops! Something went wrong.',
            message: `Not Allowed ${err.message}`,
            color: 'red',
        })

        return false

    }

}